/** @format */

import {Transition} from '@headlessui/react'
import * as Sentry from '@sentry/react'
import React, {useEffect, useMemo, useState} from 'react'

import LoaderButton from '@src/components/tailwind/LoaderButton'
import {OpenModalLink} from '@src/components/tailwind/ModalContext'
import * as Modal from '@src/components/tailwind/ModalV2'
import {UpgradeMessage} from '@src/components/tailwind/UpgradeMessage'

import {useFormApi, useLoadingContext} from '@src/contexts'
import {useFetch} from '@src/hooks/useFetch'
import * as toast from '@src/toast'
import {formbutton} from '@src/utils'

import {ExportModal} from './ExportModal'
import {ReprocessRefusal} from './ReprocessRefusal'
import {useSubmissionsContext} from './SubmissionsContext'
import {useTableModel} from './TableModel'
import {
  canOfferSelectAllMatching,
  getAcceptedOverlimitIds,
  isOverLimitNoRoomError,
  pluralize,
  scheduleOverlimitReconcile,
  type MonthlyQuota,
} from './utils'

const EXPORT_SELECTED_MODAL = 'form.submissions_v2.export-selected'

type OverlimitProcessResponse = {
  // how many of the requested submissions fit in the remaining monthly limit
  count: number
  ok: boolean
}

export function ActionBar() {
  const {
    data,
    filter,
    overlimitCount,
    removeOverlimitSubmissions,
    revalidate,
    search,
  } = useSubmissionsContext()
  const {
    clearRowSelection,
    rowSelection,
    selectAllMatching,
    setSelectAllMatching,
  } = useTableModel()
  const selectedIds = useMemo(() => Object.keys(rowSelection), [rowSelection])
  const selectedCount = selectedIds.length
  const {current: currentForm} = useFormApi()
  const searchResultsCount = data?.submissions.length ?? 0

  const isOverlimitFolder = filter.folder === 'overlimit'
  const quota: MonthlyQuota = {
    limitResetsOn: data.limit_resets_on,
    monthlyLimit: data.monthly_limit,
    remaining: data.overlimit_remaining,
  }
  // a reprocess request refused up front: the selection is bigger than the
  // room left in the monthly limit
  const [refusal, setRefusal] = useState<{
    remaining: number
    requested: number
  } | null>(null)
  const offerSelectAllMatching = canOfferSelectAllMatching({
    folder: filter.folder,
    loadedCount: data.submissions.length,
    matchingCount: overlimitCount,
    selectedCount,
  })

  const fetch = useFetch()
  const {ready} = useLoadingContext()

  useEffect(() => {
    const shouldShow = selectedCount === 0
    formbutton()('showButton', shouldShow)
  }, [selectedCount])

  async function handleDeleteSubmissions(): Promise<void> {
    try {
      await fetch(`/api-int/forms/${currentForm.hashid}/submissions`, {
        method: 'DELETE',
        payload: {
          submissions: selectedIds.map(id => +id),
        },
      })
      await handleSubmissionsUpdate()
      toast.success('Submissions deleted.')
    } catch (err) {
      Sentry.captureException(err, {
        tags: {
          form: currentForm.hashid,
          operation: 'delete submissions',
        },
      })
      toast.error('Failed to delete submissions')
    }
    ready()
  }

  async function handleFlagSubmissions(): Promise<void> {
    try {
      await fetch(`/api-int/forms/${currentForm.hashid}/submissions`, {
        method: 'PATCH',
        payload: {
          operation: {spam: filter.folder !== 'spam'},
          submissions: selectedIds.map(id => +id),
        },
      })
      await handleSubmissionsUpdate()
      toast.success('Submissions updated.')
    } catch (err) {
      // un-spamming delivers the submission: at the monthly limit the API
      // refuses, which is expected, not worth reporting
      if (isOverLimitNoRoomError(err)) {
        toast.error(err.message)
      } else {
        Sentry.captureException(err, {
          tags: {
            form: currentForm.hashid,
            operation: 'update submissions',
          },
        })
        toast.error('Failed to update submissions')
      }
    }
    ready()
  }

  async function handleOverlimitProcess(): Promise<void> {
    const requestedCount = selectAllMatching ? overlimitCount : selectedCount
    // explain and squeeze before asking the server to do part of the job
    if (quota.remaining != null && requestedCount > quota.remaining) {
      setRefusal({remaining: quota.remaining, requested: requestedCount})
      ready()
      return
    }
    try {
      const {count}: OverlimitProcessResponse = await fetch(
        `/api-int/forms/${currentForm.hashid}/submissions/overlimit`,
        {
          method: 'POST',
          payload: {
            action: 'reprocess',
            submissions: selectAllMatching ? 'all' : selectedIds.map(id => +id),
          },
        },
      )
      // drop the rows the worker is about to process; the refetches below
      // reconcile whatever it actually did
      removeOverlimitSubmissions(
        getAcceptedOverlimitIds({
          count,
          loadedIds: data.submissions.map(s => s._id),
          requestedCount,
          selectAllMatching,
          selectedIds: selectedIds.map(id => +id),
        }),
      )
      clearRowSelection()
      scheduleOverlimitReconcile(revalidate)
      // count only falls short when the room left changed since the page
      // loaded (e.g. new submissions came in)
      toast.success(
        count < requestedCount
          ? `Only ${count} of ${requestedCount} ${pluralize(requestedCount, 'submission')} still fit in your monthly limit and are being reprocessed. The rest stay in Over Limit.`
          : count === 1
            ? "Reprocessing started on 1 submission. It'll move to your Inbox once it's reprocessed."
            : `Reprocessing started on ${count} submissions. They'll move to your Inbox once they're reprocessed.`,
      )
    } catch (err) {
      // the account is still over its limit — expected, not worth reporting
      if (isOverLimitNoRoomError(err)) {
        toast.error(err.message)
      } else {
        Sentry.captureException(err, {
          tags: {
            form: currentForm.hashid,
            operation: 'process over-limit submissions',
          },
        })
        toast.error('Failed to process submissions')
      }
    }
    ready()
  }

  async function handleSubmissionsUpdate(): Promise<void> {
    await revalidate()
    clearRowSelection()
  }

  return (
    <>
      <Modal.Root isOpen={refusal != null} onClose={() => setRefusal(null)}>
        <Modal.DefaultLayout>
          <Modal.Header title="Reprocess Submissions" />
          <Modal.Content>
            <UpgradeMessage
              alignButton="end"
              feature="account_monthly_submissions"
            >
              {refusal != null && (
                <ReprocessRefusal
                  {...refusal}
                  limitResetsOn={quota.limitResetsOn}
                  monthlyLimit={quota.monthlyLimit}
                />
              )}
            </UpgradeMessage>
          </Modal.Content>
        </Modal.DefaultLayout>
      </Modal.Root>
      <ExportModal
        modal={EXPORT_SELECTED_MODAL}
        selectedIds={selectedIds}
        searchQuery={search}
        searchResultsCount={searchResultsCount}
        currentFolder={filter.folder === 'spam' ? 'spam' : 'inbox'}
      />
      <Transition
        className="pointer-events-none fixed inset-x-0 bottom-0 z-20 flex px-4"
        enter="duration-150 ease-out"
        enterFrom="translate-y-full"
        enterTo="-translate-y-4"
        leave="duration-75 ease-out"
        leaveFrom="-translate-y-4"
        leaveTo="translate-y-full"
        show={selectedCount > 0}
      >
        <div className="pointer-events-auto mx-auto flex w-full max-w-5xl flex-col items-center rounded-md bg-secondary px-6 py-3 shadow-lg sm:flex-row">
          <p className="mr-auto text-white">
            {selectAllMatching ? (
              <span>
                All {overlimitCount} over-limit{' '}
                {pluralize(overlimitCount, 'submission')} selected
              </span>
            ) : (
              <>
                <span>{selectedCount}</span>
                <span> of </span>
                <span>{data.submissions.length}</span>
                <span>
                  {' '}
                  {pluralize(data.submissions.length, 'row')} selected
                </span>
                {offerSelectAllMatching && (
                  <>
                    <span> </span>
                    <button
                      className="cursor-pointer font-bold underline"
                      onClick={() => setSelectAllMatching(true)}
                    >
                      Select all {overlimitCount} over-limit submissions
                    </button>
                  </>
                )}
              </>
            )}
          </p>

          <div className="mt-3 flex flex-wrap gap-2 self-start sm:mt-0 sm:self-center">
            <button
              className="rounded border-2 border-white px-4 py-2 font-bold text-white hover:bg-white/10"
              onClick={clearRowSelection}
            >
              Cancel
            </button>
            {isOverlimitFolder ? (
              <>
                <LoaderButton onClick={handleOverlimitProcess}>
                  Reprocess
                </LoaderButton>
                {!selectAllMatching && (
                  <LoaderButton onClick={handleDeleteSubmissions}>
                    Delete
                  </LoaderButton>
                )}
              </>
            ) : (
              <>
                <OpenModalLink
                  modal={EXPORT_SELECTED_MODAL}
                  className="buttonPrimary"
                >
                  Export
                </OpenModalLink>
                <LoaderButton onClick={handleFlagSubmissions}>
                  {filter.folder === 'spam' ? 'Not Spam' : 'Spam'}
                </LoaderButton>
                <LoaderButton onClick={handleDeleteSubmissions}>
                  Delete
                </LoaderButton>
              </>
            )}
          </div>
        </div>
      </Transition>
    </>
  )
}
